import React, {useCallback, useEffect, useState} from 'react';
import {useForm} from 'react-hook-form';
import {connect} from "react-redux";
import {affiliateNetworkActions} from "../../store/actions/affiliateNetworkAction";
import {affNetworkServices} from "../../service/affiliateNetworkService";
import {AffiliateNetworkForm} from "./AffiliateNetworkForm";
import {postbackURL} from "../../helpers/config";
import {GetPostbackLink} from "../../helpers/common";
import {useMediaQuery, useTheme} from "@mui/material";

import {AffiliateNetworkDialog} from "./AffiliateNetworkDesign";

function DuplicateAffiliateNetwork(props) {

    const [open, setOpen] = useState(true);
    const [formData, setFormData] = useState(null);
    const [affNetworkData, setAffNetworkData] = useState(null);
    const [requestError, setRequestError] = useState("");
    const [loading, setLoading] = useState(false);
    const [sharedAffiliateNetworkId, setSharedAffiliateNetworkId] = useState(0);
    const [postbackLink, setPostbackLink] = useState(postbackURL);

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

    const {register, handleSubmit, setValue, watch, control, getValues, formState: {errors}} = useForm();

    const handleClose = useCallback((event, reason) => {
        if (reason === 'backdropClick') {
            return false;
        }
        setOpen(false);
        props.history.push({pathname: "/affiliate-networks", state: {from: props.location}});
    }, [props.history, props.location]);

    const onSubmit = (data) => {
        setFormData({
            workspaceId: data?.workspace?.value,
            name: data?.name,
            sharedAffiliateNetworkId: sharedAffiliateNetworkId,
            postbackOnlyWhiteIps: data?.postbackOnlyWhiteIps,
            postbackWhiteIps: data?.postbackWhiteIps ? data?.postbackWhiteIps : "",
            allowDoubleConversions: data?.allowDoubleConversions,
            useConversionVisitTime: data?.useConversionVisitTime,
            usePostbackStatuses: data?.usePostbackStatuses,
            postbackStatusApproved: data?.postbackStatusApproved ? data?.postbackStatusApproved : "",
            postbackStatusPending: data?.postbackStatusPending ? data?.postbackStatusPending : "",
            postbackStatusDeclined: data?.postbackStatusDeclined ? data?.postbackStatusDeclined : "",
            postbackStatusRefund: data?.postbackStatusRefund ? data?.postbackStatusRefund : "",
            usePostbackProtection: data?.usePostbackProtection,
            postbackProtectionToken: data?.postbackProtectionToken,
            tags: data?.tags ? data?.tags : []
        });
    };

    useEffect(() => {
        async function findAffiliateNetworkById(id) {
            setRequestError("");
            setLoading(true);
            await affNetworkServices.find(id).then((response) => {
                if (response?.id > 0) {
                    setSharedAffiliateNetworkId(response?.sharedAffiliateNetworkId);
                    setAffNetworkData(response);
                }
            }).catch((error) => {
                setRequestError(error?.response?.data?.error || "Unable to load affiliate network. Please try again.");
            }).finally(() => {
                setLoading(false);
            });
        }

        if (props.match && props.match.params && props.match.params.hasOwnProperty("id") && affNetworkData === null) {
            findAffiliateNetworkById(props.match.params.id);
        }

    }, [affNetworkData, props]);

    useEffect(() => {
        if (affNetworkData !== null) {
            setValue("name", affNetworkData?.name + " - [copy]");
            setValue("tags", affNetworkData?.tags);
            setValue("postbackOnlyWhiteIps", affNetworkData?.postbackOnlyWhiteIps);
            setValue("allowDoubleConversions", affNetworkData?.allowDoubleConversions);
            setValue("useConversionVisitTime", affNetworkData?.useConversionVisitTime);
            setValue("usePostbackStatuses", affNetworkData?.usePostbackStatuses);
            setValue("usePostbackProtection", affNetworkData?.usePostbackProtection);

            setPostbackLink(
                GetPostbackLink(
                    affNetworkData?.sharedAffiliateNetwork?.clickIdPlaceholder,
                    affNetworkData?.sharedAffiliateNetwork?.payoutPlaceholder,
                    affNetworkData?.sharedAffiliateNetwork?.transactionIdPlaceholder,
                    affNetworkData?.sharedAffiliateNetwork?.statusPlaceholder
                )
            );

            setTimeout(() => {
                setValue("postbackWhiteIps", affNetworkData?.postbackWhiteIps);
                setValue("postbackStatusApproved", affNetworkData?.postbackStatusApproved);
                setValue("postbackStatusPending", affNetworkData?.postbackStatusPending);
                setValue("postbackStatusDeclined", affNetworkData?.postbackStatusDeclined);
                setValue("postbackStatusRefund", affNetworkData?.postbackStatusRefund);
                setValue("postbackProtectionToken", affNetworkData?.postbackProtectionToken);
            });
        }
    }, [affNetworkData, setValue]);

    useEffect(() => {
        async function postData() {
            setRequestError("");
            setLoading(true);
            await affNetworkServices.create(formData).then((response) => {
                if (response?.id > 0) {
                    props.rerender(true);
                    setLoading(false);
                    handleClose();
                } else {
                    setLoading(false);
                }
            }).catch((error) => {
                setRequestError(error?.response?.data?.error || "Unable to create affiliate network. Please try again.");
                setLoading(false);
            });
        }

        if (formData !== null) {
            postData();
        }

    }, [handleClose, formData, props]);

    return (
        <AffiliateNetworkDialog open={open}
                onClose={handleClose}
                fullWidth={true}
                fullScreen={fullScreen}
                maxWidth={"md"}
                aria-labelledby="affiliate-network-form-title"
        >
            <AffiliateNetworkForm
                title="Duplicate affiliate network"
                description={affNetworkData ? `Create a copy of ${affNetworkData?.name} with your own settings.` : "Create a copy with your own settings."}
                submitLabel="Create copy"
                handleClose={handleClose}
                requestError={requestError}
                onSubmit={onSubmit}
                control={control}
                register={register}
                errors={errors}
                handleSubmit={handleSubmit}
                loading={loading}
                postbackLink={postbackLink}
                watch={watch}
                formData={affNetworkData}
                getValues={getValues}
                setValue={setValue}
            />
        </AffiliateNetworkDialog>
    );
}

const mapDispatchToProps = {
    rerender: affiliateNetworkActions.rerender
};

export default connect(null, mapDispatchToProps)(DuplicateAffiliateNetwork);